import { DictionaryField, ImageField, LinkField, StringField, TBaseObj } from '../../../../../types/types';
import {
  TNavigationItemElementFields,
  TNavigationItemFields,
  TNavigationItemColumnField,
  TMenuLinkTypeMapper,
  TSubMenuElementType,
  SecondaryLinkFields,
  TNavigationItemTypeMapper,
  TNavigationItemType,
} from '../types/navigationItem.types';

export const dictionaryFieldMapper: <T>(field: DictionaryField<T, any> | undefined) => T = (field) =>
  field?.fields?.Key?.value || field?.fields?.Phrase?.value;

export const NavigationItemTypeMapper: TNavigationItemTypeMapper = {
  menu: 'headerMenuDropdownLink',
  link: 'headerMenuButtonLink',
};

export const MenuLinkTypeMapper: TMenuLinkTypeMapper = {
  primaryLink: 'headerSubmenuPrimaryBoldLink',
  imageLink: 'headerSubmenuPrimaryImageLink',
  secondaryLink: 'headerSubmenuSecondarySimpleTextLink',
  secondaryLinkWithBold: 'headerSubmenuSecondaryBoldTextLink',
};

export class BaseNavigationItemModel {
  protected readonly fields: TNavigationItemFields;

  public readonly type: TNavigationItemType;
  public readonly menuTitle: StringField;

  constructor(fields: TNavigationItemFields) {
    this.fields = fields;
    this.type = dictionaryFieldMapper(fields?.linkType) || NavigationItemTypeMapper.link;
    this.menuTitle = fields?.menuButton || { value: '' };
  }
}

export class NavigationItemLinkModel extends BaseNavigationItemModel {
  readonly link: LinkField;
  public readonly displayName?: string;
  public readonly id: string;

  constructor(fields: TNavigationItemFields, id: string, displayName = '') {
    super(fields);
    this.link = fields?.link || { value: { href: '#', text: 'link' } };
    this.displayName = displayName;
    this.id = id;
  }

  get linkId(): string {
    return this.id ?? '';
  }

  get linkUrl() {
    return this.link?.value?.href ?? '#';
  }
}

export class BaseMenuLinkModel {
  protected readonly _fields: TNavigationItemElementFields | SecondaryLinkFields | undefined;
  public readonly type: TSubMenuElementType;
  public readonly link: LinkField;
  public readonly id?: string;
  constructor(type: TSubMenuElementType, fields: TNavigationItemElementFields | SecondaryLinkFields) {
    this.type = type;
    this._fields = fields;
    this.link = fields?.link || { value: { href: '#' } };
  }

  get linkId(): string {
    return this._fields?.id || '';
  }

  get linkUrl(): string {
    return this.link.value?.href ?? '#';
  }
  get linkText(): string {
    return this.link.value?.text ?? 'link';
  }

  get linkOtherProps(): TBaseObj {
    const otherProps: TBaseObj = {};
    if (this.link.value) {
      const value = this.link.value;
      if (value.title) {
        otherProps.title = value.title;
      }
      if (value?.target) {
        otherProps.target = value?.target;
      }
    }
    return otherProps;
  }
}

export class PrimaryLinkModel extends BaseMenuLinkModel {
  public readonly secondaryLinks: (SecondaryLinkModel | SecondaryLinkWithBoldModel)[];
  public readonly hasSecondaryLinks: boolean;
  public readonly viewAll: StringField;
  constructor(groupLinkFields: TNavigationItemElementFields) {
    super(MenuLinkTypeMapper.primaryLink, groupLinkFields);
    if (groupLinkFields?.secondaryLinks && Array.isArray(groupLinkFields.secondaryLinks) && groupLinkFields.secondaryLinks.length) {
      this.hasSecondaryLinks = true;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      this.secondaryLinks = groupLinkFields.secondaryLinks.map((secondaryLink) => newSubMenuElement(secondaryLink.fields));
    } else {
      this.secondaryLinks = [];
      this.hasSecondaryLinks = false;
    }
    this.viewAll = groupLinkFields?.viewAll || { value: 'View all' };
  }

  get viewAllText(): string {
    return this.viewAll.value ?? 'View all';
  }

  get hasViewAll(): boolean {
    return this.viewAllText !== '';
  }
}

export class SecondaryLinkModel extends BaseMenuLinkModel {
  constructor(fields: SecondaryLinkFields) {
    super(MenuLinkTypeMapper.secondaryLink, fields);
  }
}

export class ImageLinkModel extends BaseMenuLinkModel {
  public readonly image: ImageField;
  constructor(fields: TNavigationItemElementFields) {
    super(MenuLinkTypeMapper.imageLink, fields);

    this.image = fields?.image || { value: { src: '' } };
  }
}

export class SecondaryLinkWithBoldModel extends BaseMenuLinkModel {
  public readonly divider: StringField;
  public readonly boldText: StringField;
  constructor(fields: SecondaryLinkFields) {
    super(MenuLinkTypeMapper.secondaryLinkWithBold, fields);

    this.divider = fields.divider ?? { value: '' };
    this.boldText = fields.boldSecondaryText ?? { value: '' };
  }
}

export class NavigationItemColumnModel {
  private readonly sitecoreElements: TNavigationItemColumnField | undefined;
  public readonly elements: TMenuLinkModel[];
  public readonly id?: string;
  constructor(sitecoreElements: TNavigationItemColumnField | undefined, id?: string) {
    this.sitecoreElements = sitecoreElements;
    this.id = id;

    if (this.sitecoreElements && Array.isArray(this.sitecoreElements)) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      this.elements = this.sitecoreElements.map((element) => newSubMenuElement(element.fields));
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      this.elements = [new PrimaryLinkModel(undefined)];
      // TODO: have to check above line about why undefined is required
    }
  }
}
export class NavigationItemMenuModel extends BaseNavigationItemModel {
  public readonly columns: NavigationItemColumnModel[] = [];
  public readonly isLastColumnGray: boolean;
  public readonly type: TNavigationItemType;
  public readonly displayName?: string;
  public readonly id?: string;

  constructor(fields: TNavigationItemFields, id: string, displayName = '') {
    super(fields);
    this.displayName = displayName;
    this.id = id;
    if (fields?.section1) {
      this.columns.push(new NavigationItemColumnModel(fields?.section1, id));
    }

    if (fields?.section2) {
      this.columns.push(new NavigationItemColumnModel(fields?.section2, id));
    }

    if (fields?.section3) {
      this.columns.push(new NavigationItemColumnModel(fields?.section3, id));
    }

    if (fields?.section4) {
      this.columns.push(new NavigationItemColumnModel(fields?.section4, id));
    }
    this.isLastColumnGray = fields?.isSection4Gray?.value ?? false;
  }
}

export type TNavigationItemModel = NavigationItemLinkModel | NavigationItemMenuModel;

export const newNavigationItemModel = (fields: TNavigationItemFields, id: string,  displayName = ''): TNavigationItemModel => {
  switch (dictionaryFieldMapper(fields?.linkType)) {
    case NavigationItemTypeMapper.link:
      return new NavigationItemLinkModel(fields, id, displayName);
    case NavigationItemTypeMapper.menu: {
      return new NavigationItemMenuModel(fields, id, displayName);
    }
    default: {
      return new NavigationItemLinkModel(fields, id, displayName);
    }
  }
};

export const newSubMenuElement = (elementFields: TNavigationItemElementFields | SecondaryLinkFields | undefined): TMenuLinkModel => {
  switch (dictionaryFieldMapper(elementFields?.linkType)) {
    case MenuLinkTypeMapper.primaryLink:
      return new PrimaryLinkModel(elementFields as TNavigationItemElementFields);
    case MenuLinkTypeMapper.secondaryLink:
      return new SecondaryLinkModel(elementFields as SecondaryLinkFields);
    case MenuLinkTypeMapper.imageLink:
      return new ImageLinkModel(elementFields as TNavigationItemElementFields);
    case MenuLinkTypeMapper.secondaryLinkWithBold:
      return new SecondaryLinkWithBoldModel(elementFields as SecondaryLinkFields);
    default:
      return new PrimaryLinkModel(elementFields as TNavigationItemElementFields);
  }
};

export type TMenuLinkModel = PrimaryLinkModel | SecondaryLinkModel | SecondaryLinkWithBoldModel | ImageLinkModel;
